import { getSurveyIllustration } from 'features/onboarding/utils/getSurveyIllustration';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import RadioIcon from 'shared/components/RadioIcon/RadioIcon';
import Button from 'shared/components/vxl/Button/Button';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isAndroid } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

type PaymentOption = {
  id: string;
  labelText: string;
  clickAnalytics?: {
    name: string;
    properties: { key: string; value: string }[];
  } | null;
};

const navBarSize = 56;

interface HowDoYouGetPaidProps {
  optionsData: PaymentOption[];
  headlineText: string;
  bodyText: string;
  onContinueButton: (selectedOption: string | undefined) => void;
  isLoading: boolean;
}

const HowDoYouGetPaidSelection = ({
  optionsData,
  headlineText,
  bodyText,
  onContinueButton,
  isLoading,
}: HowDoYouGetPaidProps) => {
  const { bottom, top } = useSafeAreaInsets();

  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    undefined,
  );

  const { segmentClient } = useAnalyticsConfiguration();
  const { t: translatedText } = useTranslation('onboarding');

  const containerContentStyle = {
    paddingTop: 0,
  };

  return (
    <View style={styles.container}>
      <View style={{ height: top + navBarSize }} />
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={containerContentStyle}
        >
          {headlineText ? (
            <TitleXLText style={styles.headlineText}>
              {headlineText}
            </TitleXLText>
          ) : null}
          {bodyText ? (
            <BodyBaseText style={styles.bodyText}>{bodyText}</BodyBaseText>
          ) : null}
          <View style={styles.radioCardsContainer}>
            {optionsData.map((option) => (
              <Pressable
                style={[
                  styles.pressableContainer,
                  option.id === selectedOption && styles.selected,
                ]}
                id={option.id}
                key={option.id}
                onPress={() => {
                  setSelectedOption(option.id);
                }}
                accessible
                accessibilityState={{ checked: option.id === selectedOption }}
                accessibilityLabel={option.labelText}
                accessibilityRole="radio"
              >
                <View style={styles.contentContainer}>
                  <RadioIcon selected={option.id === selectedOption} />
                  <View style={styles.contentInner}>
                    <BodySmBoldText style={styles.labelText}>
                      {option.labelText}
                    </BodySmBoldText>
                  </View>
                </View>
                <View style={styles.illustrationContainer}>
                  <Illustration
                    illustration={getSurveyIllustration(option.id)}
                  />
                </View>
              </Pressable>
            ))}
          </View>
        </ScrollView>
        <View
          style={[
            styles.footerContainer,
            {
              paddingBottom: isAndroid
                ? bottom + theme.spacing.m
                : bottom + theme.spacing.l,
            },
          ]}
        >
          <Button
            label={translatedText('onboardingOptionsScreen.continue')}
            onPress={() => {
              const selectedOptionData = optionsData.find(
                (opt) => opt.id === selectedOption,
              );
              if (selectedOptionData?.clickAnalytics) {
                const { name, properties } = parseGQLAnalyticsEvent(
                  selectedOptionData.clickAnalytics,
                );
                void segmentClient.logEvent(name, properties);
              }
              onContinueButton(selectedOption);
            }}
            isDisabled={!selectedOption}
          />
        </View>
      </View>
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('loading.titleText')}
          />
        }
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyText: {
    paddingTop: 10,
    paddingBottom: 0,
    padding: theme.spacing.m,
    textAlign: 'left',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  pressableContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  contentContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  contentInner: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  illustrationContainer: {
    height: 56,
    width: 56,
  },
  labelText: {
    color: theme.color.interactive.primary,
  },
  selected: {
    borderColor: theme.color.interactive.primary,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
    padding: theme.spacing.m,
  },
  headlineText: {
    padding: theme.spacing.m,
    textAlign: 'left',
    paddingBottom: 0,
  },
  radioCardsContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  scrollContainer: {
    flex: 1,
  },
});

export default HowDoYouGetPaidSelection;
